import React, { useState, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindow,
  Circle,
} from "@react-google-maps/api";
import { Box, Typography, CircularProgress, Card, CardMedia } from "@mui/material";
import { ASSET_BASE } from "../../utils/baseUrls";

const MAPS_LIBRARIES = ["places"];

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "12px",
};

const PlotMap = ({ lat, lng, map_set = [], propertyName, showRadius = false }) => {
  const [selectedPlot, setSelectedPlot] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBMORJwqAENHll476u4UXGJmV_bSFJycA8",
    libraries: MAPS_LIBRARIES,
  });

  const center = {
    lat: parseFloat(lat) || 0,
    lng: parseFloat(lng) || 0,
  };

  const [map, setMap] = useState(null);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          bgcolor: "#f5f5f5",
          borderRadius: 3,
        }}
      >
        <CircularProgress size={40} sx={{ color: "#608900" }} />
      </Box>
    );
  }

  const mainMarkerIcon = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
  const nearbyMarkerIcon = "https://maps.google.com/mapfiles/ms/icons/red-dot.png";

  return (
    <>
      <style>{`
        .map-tag-label {
          background-color: white;
          padding: 3px 8px;
          border-radius: 6px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.15);
          font-weight: 700;
          font-size: 11px;
          font-family: 'Inter', 'Roboto', sans-serif;
          transform: translateY(-28px);
          white-space: nowrap;
          pointer-events: none;
        }
        .main-tag {
          color: #608900;
          border: 1px solid #608900;
        }
        .nearby-tag {
          color: #d32f2f;
          border: 1px solid #ffcdd2;
        }
      `}</style>
      <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={13}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {/* 5km Radius Circle */}
      {showRadius && (
        <Circle
          center={center}
          radius={5000} // 5000 meters = 5km
          options={{
            fillColor: "#608900",
            fillOpacity: 0.1,
            strokeColor: "#608900",
            strokeOpacity: 0.5,
            strokeWeight: 1,
            clickable: false,
          }}
        />
      )}

      {/* Main Plot Marker */}
      <MarkerF
        position={center}
        onClick={() => setSelectedPlot({ name: propertyName, lat: center.lat, lng: center.lng, isMain: true })}
        zIndex={100}
      />

      {/* Nearby Plots Markers */}
      {map_set.map((plot) => (
        <MarkerF
          key={plot.id}
          position={{
            lat: parseFloat(plot.lat),
            lng: parseFloat(plot.lng || plot.long)
          }}
          onClick={() => setSelectedPlot({
            ...plot,
            name: plot.property_name || plot.name,
            address: plot.location || plot.address,
            image: Array.isArray(plot.image) ? plot.image : plot.thumbnail ? [plot.thumbnail] : []
          })}
        />
      ))}

      {/* Info Window */}
      {selectedPlot && (
        <InfoWindow
          position={{
            lat: parseFloat(selectedPlot.lat),
            lng: parseFloat(selectedPlot.lng) || parseFloat(selectedPlot.long),
          }}
          onCloseClick={() => setSelectedPlot(null)}
        >
          <Box sx={{ p: 0.5, maxWidth: 220 }}>
            {selectedPlot.image && selectedPlot.image[0] && (
               <CardMedia
                component="img"
                height="100"
                image={selectedPlot.image[0].startsWith("http") ? selectedPlot.image[0] : `${ASSET_BASE}${selectedPlot.image[0]}`}
                alt={selectedPlot.name}
                sx={{ borderRadius: 1, mb: 1, objectFit: "cover" }}
              />
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#608900" }}>
              {selectedPlot.name}
            </Typography>
            {selectedPlot.address && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedPlot.address}
              </Typography>
            )}
            {selectedPlot.price && (
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: "#2E7D32" }}>
                ₹{Number(selectedPlot.price).toLocaleString("en-IN")}
              </Typography>
            )}
            {selectedPlot.isMain && (
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, mt: 0.5, display: "block" }}>
                    Current Property
                </Typography>
            )}
          </Box>
        </InfoWindow>
      )}
    </GoogleMap>
    </>
  );
};

export default React.memo(PlotMap);
